import React, { useState } from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { IReduxState } from 'interfaces/IReduxState'
import { useDispatch, useSelector } from 'react-redux'
import MuiAlert from '@mui/material/Alert'
import {
  IIngredientRequest,
  IngredientRequest,
  IngredientRequestSourceOrDestination,
} from 'interfaces/IIngredientRequest'
import API from '@aws-amplify/api'
import IngredientFinder from './ingredientFinder'
import { IIngredientOrder } from 'interfaces/IIngredientOrder'
import { VALUE_IN, VALUE_OUT } from 'enums/stockCard'
import LoadBatchAutocomplete from 'shared/autocomplete/loadBatchAutocomplete'
import { IBatch } from 'interfaces/IPremix'

export default function IngredientRequestForm() {
  const user = useSelector((state: IReduxState) => state.currentUser || {})
  const [ingredientRequest, setIngredientRequest] =
    React.useState<IIngredientRequest>(new IngredientRequest({}))
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasValidationErrors, setHasValidationErrors] = React.useState(false)
  const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null)
  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const getMissingFields = (request: IIngredientRequest): string[] => {
    const missing: string[] = []

    if (!request.inOutDirection) missing.push('In/Out')
    if (!request.sourceOrDestination) missing.push('Dari/Tujuan')

    const hasIngredients = Array.isArray(request.ingredients)
      ? request.ingredients.length > 0
      : false
    if (!hasIngredients) missing.push('Bahan Baku (dengan Qty)')

    // Notes required for no-cost flows
    const requiresNotes = [
      IngredientRequestSourceOrDestination.inNoCost,
      IngredientRequestSourceOrDestination.outNoCost,
      IngredientRequestSourceOrDestination.productionAdditionalRequest,
      IngredientRequestSourceOrDestination.outWithCost,
    ].includes(
      // @ts-ignore safeguard when undefined
      request.sourceOrDestination as any
    )

    if (requiresNotes && !request.notes) missing.push('Notes')

    return missing
  }

  const getValidationErrors = () => {
    const errors: string[] = []
    const missing = getMissingFields(ingredientRequest)
    if (missing.length > 0) {
      errors.push(`Lengkapi data berikut: ${missing.join(', ')}`)
    }
    if (hasValidationErrors) {
      errors.push('Ada bahan baku dengan qty yang melebihi stok')
    }
    return errors
  }

  const isAllFieldsComplete = (ingredientRequest: IIngredientRequest) => {
    return getValidationErrors().length === 0
  }

  const saveIngredientRequest = () => {
    setIsLoading(true)
    API.post('products', `/ingredients/ingredientRequest`, {
      body: {
        ...ingredientRequest,
        batchNumber: selectedBatch?.batchNumber,
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil membuat request bahan baku.')
        setIsSuccessSnackbarOpen(true)
        setIngredientRequest(new IngredientRequest())
        window.location.reload()
      })
      .catch((error) => {
        setAlertMessage('Gagal membuat request bahan baku.')
        setIsFailureSnackbarOpen(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleIngredientRequestFieldChange = (
    value: any,
    key: string
  ): void => {
    setIngredientRequest(
      (ingredientRequestData: IIngredientRequest): IIngredientRequest => ({
        ...ingredientRequestData,
        [key]: value,
      })
    )
  }

  const onSelectedIngredientChange = (
    ingredients: Partial<IIngredientOrder>[]
  ) => {
    handleIngredientRequestFieldChange(ingredients, 'ingredients')
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Request Bahan Baku In/Out' })
  }, [dispatch])

  return (
    <React.Fragment>
      <Paper>
        <Box p={2}>
          <List disablePadding>
            <ListItem disableGutters>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      In/Out
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={ingredientRequest.inOutDirection}
                  onChange={(e) =>
                    handleIngredientRequestFieldChange(
                      e.target.value,
                      'inOutDirection'
                    )
                  }
                >
                  <FormControlLabel
                    value={VALUE_IN}
                    control={<Radio color="primary" />}
                    label={'Masuk ke Gudang Bahan Baku'}
                  />
                  <FormControlLabel
                    value={VALUE_OUT}
                    control={<Radio color="primary" />}
                    label={'Keluar dari Gudang Bahan Baku'}
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem disableGutters>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      Dari/Tujuan
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={ingredientRequest.sourceOrDestination}
                  onChange={(e) =>
                    handleIngredientRequestFieldChange(
                      e.target.value,
                      'sourceOrDestination'
                    )
                  }
                >
                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.rndOut}
                      control={<Radio color="primary" />}
                      label={'RnD'}
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.rndIn}
                      control={<Radio color="primary" />}
                      label={'RnD'}
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={
                        IngredientRequestSourceOrDestination.productionAdditionalRequest
                      }
                      control={<Radio color="primary" />}
                      label={'Produksi Request Tambahan'}
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.adjustment}
                      control={<Radio color="primary" />}
                      label={'Adjustment'}
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.stokQc}
                      control={<Radio color="primary" />}
                      label={'Stok QC'}
                    />
                  )}

                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.adjustment}
                      control={<Radio color="primary" />}
                      label={'Adjustment'}
                    />
                  )}

                  {ingredientRequest.inOutDirection === VALUE_IN && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.inNoCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang masuk tanpa biaya (wajib tulis notes dari siapa)'
                      }
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.outWithCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang dikeluarkan dengan biaya (wajib tulis tujuan mana di notes)'
                      }
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.outNoCost}
                      control={<Radio color="primary" />}
                      label={
                        'Barang di keluarkan tanpa biaya (wajib tulis notes untuk siapa)'
                      }
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={
                        IngredientRequestSourceOrDestination.productionAdditionalRequest
                      }
                      control={<Radio color="primary" />}
                      label={'Produksi Request Tambahan'}
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={IngredientRequestSourceOrDestination.stokQc}
                      control={<Radio color="primary" />}
                      label={'Stok QC'}
                    />
                  )}
                  {ingredientRequest.inOutDirection === VALUE_OUT && (
                    <FormControlLabel
                      value={
                        IngredientRequestSourceOrDestination.purchaseReturn
                      }
                      control={<Radio color="primary" />}
                      label={'Purchase Return'}
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem disableGutters>
              <IngredientFinder
                showQty
                selectedIngredients={ingredientRequest.ingredients}
                getSelectedIngredients={onSelectedIngredientChange}
                editableQty
                showInventory
                showSelectedIngredients
                onValidationChange={setHasValidationErrors}
                validateStock={ingredientRequest.inOutDirection === VALUE_OUT}
              />
            </ListItem>
            <ListItem disableGutters>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={ingredientRequest.notes}
                fullWidth
                label={'Notes Peminta'}
                error={getMissingFields(ingredientRequest).includes('Notes')}
                helperText={
                  getMissingFields(ingredientRequest).includes('Notes')
                    ? 'Wajib diisi (Input No Bets untuk Produksi Request Tambahan)'
                    : ''
                }
                onChange={(e) =>
                  handleIngredientRequestFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <LoadBatchAutocomplete onChange={(e) => setSelectedBatch(e)} />
            </ListItem>
            <ListItem disableGutters>
              <Typography>Request Dibuat Oleh: {user.username}</Typography>
            </ListItem>
          </List>
          {/* Missing fields helper */}
          {getMissingFields(ingredientRequest).length > 0 && (
            <Box mt={2}>
              <MuiAlert severity="warning" elevation={0} variant="outlined">
                Lengkapi data berikut sebelum menyimpan:{' '}
                {getMissingFields(ingredientRequest).join(', ')}
              </MuiAlert>
            </Box>
          )}

          {/* Validation errors */}
          {hasValidationErrors && (
            <Box mt={2}>
              <MuiAlert severity="error" elevation={0} variant="outlined">
                Ada bahan baku dengan qty yang melebihi stok. Mohon perbaiki
                sebelum menyimpan.
              </MuiAlert>
            </Box>
          )}

          <Box
            mt={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              disabled={!isAllFieldsComplete(ingredientRequest) || isLoading}
              onClick={saveIngredientRequest}
            >
              Save
            </Button>
            {!isAllFieldsComplete(ingredientRequest) && (
              <Box mt={1}>
                {getValidationErrors().map((err) => (
                  <Typography
                    key={err}
                    variant="caption"
                    color="error"
                    display="block"
                    align="right"
                  >
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
